import React, { Fragment, useEffect, useState } from "react";
import ModalBox from "../../components/modal/ModalBox";
import { Input, Select, TextArea } from "../../components/Form";
import useButtonLoader from "../../hooks/UseButtonLoader";
import {
  allowAlphaNumericWithSpace,
  optimizeFunction,
  regex,
} from "../../utils/regex";
import { Avatar, IconButton, Chip, FormControl, FormLabel, RadioGroup, FormControlLabel, Radio } from "@mui/material";
import { charLimit } from "../../utils/charLimit";
import { toastMessage } from "../../utils/toastMessage";
import commonMessages from "../../utils/commonMessages";
import {
  checkImageAspectRatio,
  checkImageDimensions,
  getImageDimension,
} from "../../utils/checkImageDimension";
import { updateStoryApi } from "../../services/exploreManagement";
import VideoPreview from "../../components/videoPreview/VideoPreview";
import VideoThumbnail from "react-video-thumbnail";
import { BaseToImage } from "../../utils/BaseToImage";
import CloseIcon from '@mui/icons-material/Close';
import configData from "../../config/configData";
import { convertSecToMin } from "../../utils/convertSecToMin";
const {
  ADD_BANNER_TITLE,
  CHOOSE_CATEGORY,
  IMAGE_FORMAT,
  MESSAGE_SIZE,
  VIDEO_FORMAT,
  VALID_AUDIO,
  AUDIO_EXCEED_SIZE,
  EXCEED_SIZE,
  ERROR_AUDIO_LOADING
} = commonMessages;
const { imageFileRegex, videoFileRegex, audioFileRegex } = regex;

const EditStories = ({
  show,
  close,
  fetchExploreHandler,
  selectedExploreDetail,
  setSelectedExploreDetail,
}) => {
  const [images, setImages] = useState([]);
  const [imageSize, setImageSize] = useState({
    height: selectedExploreDetail?.height || "",
    width: selectedExploreDetail?.width || ""
  });
  const [videoThumbnail, setVideoThumbnail] = useState("");
  const [exploreImage, setExploreImage] = useState(null);
  const [music, setMusic] = useState('');
  const [musicDuration, setMusicDuration] = useState("");
  const [error, setError] = useState({});
  const [buttonref, buttonLoader] = useButtonLoader("Update");
  const [updateStory, setUpdateStory] = useState({
    id: selectedExploreDetail._id,
    description: selectedExploreDetail.description,
    title: selectedExploreDetail.title,
    hastag: selectedExploreDetail.hastag?._id || selectedExploreDetail.hastag,
  });
  const [postType, setPostType] = useState(selectedExploreDetail.postType || "image");
  const [scheduledAt, setScheduledAt] = useState(selectedExploreDetail.scheduledAt ? new Date(selectedExploreDetail.scheduledAt).toISOString().slice(0, 16) : "");
  const [isScheduled, setIsScheduled] = useState(selectedExploreDetail.isScheduled || false);

  //----------validation------------
  const validation = () => {
    let errorMessage = {};
    let status = true;

    if (!updateStory.title) {
      errorMessage["title"] = "Please enter title";
      status = false;
    }

    if (!updateStory.hastag) {
      errorMessage["hastag"] = "Add hashtag";
      status = false;
    }
    if (!updateStory.description) {
      errorMessage["description"] = "Add description";
      status = false;
    }

    // Post type specific validation
    if (postType === "image" && images.length === 0) {
      errorMessage["image"] = "Please add at least one image";
      status = false;
    }
    
    if (postType === "audio" && (!music || images.length === 0)) {
      if (!music) errorMessage["music"] = "Please add audio file";
      if (images.length === 0) errorMessage["image"] = "Please add at least one image";
      status = false;
    }
    
    if (postType === "video" && images.length === 0) {
      errorMessage["image"] = "Please add at least one video";
      status = false;
    }

    // Schedule validation
    if (isScheduled && !scheduledAt) {
      errorMessage["scheduledAt"] = "Please select schedule date and time";
      status = false;
    }

    if (isScheduled && scheduledAt && new Date(scheduledAt) <= new Date()) {
      errorMessage["scheduledAt"] = "Schedule date must be in the future";
      status = false;
    }

    setError(errorMessage);
    return status;
  };

  //------------------onChange-input-value---------------------------
  const imageHandler = async (e) => {
    e.preventDefault();
    const { files } = e.target;
    console.log(files, "Files");

    if (files.length === 0) {
      return;
    }

    // Convert FileList to Array for easier handling
    const fileArray = Array.from(files);
    const validFiles = [];
    const invalidFiles = [];

    for (const file of fileArray) {
      const fileName = file.name.toLowerCase();
      const isGif = fileName.endsWith(".gif");
      const isImage = imageFileRegex.test(fileName);
      const isVideo = videoFileRegex.test(fileName);

      if (isGif || isImage || isVideo) {
        if (isGif) {
          const validAspectRatio = await checkImageAspectRatio(file);
          if (validAspectRatio) {
            validFiles.push(file);
          } else {
            invalidFiles.push(file.name);
          }
        } else if (isImage) {
          validFiles.push(file);
          // Get dimensions for the first image
          if (images.length === 0 && validFiles.length === 1) {
            const img = await getImageDimension(file);
            setImageSize(img);
          }
        } else if (isVideo) {
          validFiles.push(file);
        }
      } else {
        invalidFiles.push(file.name);
      }
    }

    if (invalidFiles.length > 0) {
      toastMessage(
        `Invalid format for: ${invalidFiles.join(", ")}. Only GIF, images and videos supported`,
        "error",
        "InvalidFormat"
      );
    }

    if (validFiles.length > 0) {
      // Add new files to existing images
      setImages(prevImages => [...prevImages, ...validFiles]);
    }
    
    // Clear the input so user can select same file again if needed
    e.target.value = "";
  };

  const clearInputAndImageState = (e) => {
    e.target.value = "";
    setImages([]);
  };

  // Remove individual image
  const removeImage = (indexToRemove) => {
    setImages(prevImages => prevImages.filter((_, index) => index !== indexToRemove));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    let updatedValue = value;
    if (name === "title") {
      updatedValue = allowAlphaNumericWithSpace(value);
    }
    setUpdateStory({ ...updateStory, [name]: updatedValue });
  };

  //------------------modal-close--------------------------
  const modalClose = () => {
    setImages([]);
    setSelectedExploreDetail({});
    setError({});
    setPostType("image");
    setScheduledAt("");
    setIsScheduled(false);
    setMusic("");
    setMusicDuration("");
    setVideoThumbnail(null);
    setExploreImage(null);
    close();
  };
  //-------------Add Music--------------------------------
  const resetInputAndMusic = (e) => {
    e.target.value = ''
    setMusic('')
  }

  const audioHandler = (e) => {
    e.preventDefault()

    const maxSizeInBytes = configData.audio_legth
    const maxDurationInSeconds = configData.audio_duration
    const file = e.target?.files[0]

    if (!file) {
      resetInputAndMusic(e)
      return
    }

    if (!audioFileRegex.test(file.name)) {
      resetInputAndMusic(e)
      toastMessage(VALID_AUDIO, 'error', 'addAudio')
      return
    }

    if (file.size > maxSizeInBytes) {
      resetInputAndMusic(e)
      toastMessage(EXCEED_SIZE, 'error', 'addAudio')
      return
    }

    const audioElement = document.createElement('audio')
    audioElement.src = URL.createObjectURL(file)
    audioElement.onloadedmetadata = () => {
      if (audioElement.duration > maxDurationInSeconds) {
        resetInputAndMusic(e)
        toastMessage(AUDIO_EXCEED_SIZE, 'error', 'addAudio')
      } else {
        setMusicDuration(convertSecToMin(audioElement.duration));
        setMusic(file)
      }
    }
    audioElement.onerror = () => {
      resetInputAndMusic(e)
      toastMessage(ERROR_AUDIO_LOADING, 'error', 'addAudio')
    }
  }

  //--------------Update-story------------------------
  const updateStoryHandler = async () => {
    const status = validation();
    if (status) {
      try {
        buttonLoader(true);
        const formData = new FormData();
        formData.append("exploreId", updateStory.id);
        formData.append("title", updateStory.title);
        formData.append("hastag", updateStory.hastag);
        formData.append("description", updateStory.description);
        formData.append("postType", postType);
        
        // Add scheduling data
        if (isScheduled && scheduledAt) {
          formData.append("scheduledAt", scheduledAt);
          formData.append("isScheduled", "true");
        } else {
          formData.append("isScheduled", "false");
        }
        
        if (videoThumbnail) {
          formData.append("videoThumbnail", BaseToImage(videoThumbnail));
        }
        if(exploreImage){
          formData.append("exploreImage",exploreImage);
        }
        if(imageSize?.height && imageSize?.width){
          formData.append("heigth", imageSize?.height);
          formData.append("width", imageSize?.width);
        }
        if(music){
          formData.append("exploreAudio",music)
        }
        
        // Append multiple images
        images.forEach((image, index) => {
          if (image && image?.name) {
            formData.append("exploreMedia", image);
          }
        });
        
        console.log('formData : ', formData);
        const { data } = await updateStoryApi(formData);
        if (data.code === 200) {
          toastMessage(data.message, "success", "updateStory");
          fetchExploreHandler();
          modalClose();
        } else {
          toastMessage(data.message, "error", "updateStory");
        }
      } catch (error) {
        console.log("error", error);
      } finally {
        buttonLoader(false);
      }
    }
  };

  useEffect(() => {
    setUpdateStory({
      id: selectedExploreDetail._id,
      description: selectedExploreDetail.description,
      title: selectedExploreDetail.title,
      hastag: selectedExploreDetail.hastag?._id || selectedExploreDetail.hastag,
    });
    setPostType(selectedExploreDetail.postType || "image");
    setScheduledAt(selectedExploreDetail.scheduledAt ? new Date(selectedExploreDetail.scheduledAt).toISOString().slice(0, 16) : "");
    setIsScheduled(selectedExploreDetail.isScheduled || false);
    
    // Convert single image to array format for consistency
    if (selectedExploreDetail.media) {
      if (Array.isArray(selectedExploreDetail.media)) {
        setImages(selectedExploreDetail.media);
      } else {
        setImages([selectedExploreDetail.media]);
      }
    } else {
      setImages([]);
    }
    setVideoThumbnail(selectedExploreDetail.videoThumbnail);
    setMusic(selectedExploreDetail.music || '');
    setMusicDuration(selectedExploreDetail.musicDuration || "");
    setExploreImage(selectedExploreDetail.exploreImage || null);
  }, [selectedExploreDetail]);

  console.log(images,"Images");

  return (
    <ModalBox
      status={show}
      onClose={modalClose}
      title={"Edit Stories"}
      closeIcon
      custom
      size={"sm"}
      customizedFooter={
        <div className="model_custom_footer">
          {/* <p>{MESSAGE_SIZE}</p> */}
          <button
            className="btn_size "
            ref={buttonref}
            onClick={updateStoryHandler}
          >
            Update
          </button>
        </div>
      }
    >
      <div>
        <div className="form-group mb-2">
          <Input
            type="text"
            name="title"
            value={updateStory?.title || ""}
            required
            onChange={(e) => handleChange(e)}
            placeholder="Title"
            className={`form-control `}
            label={"Title"}
            error={!updateStory.title && error.title}
            maxLength={30}
          />
        </div>
        <div className="form-group mb-2">
          <Input
            name="hastag"
            type="text"
            placeholder="Hashtag"
            value={updateStory?.hastag || ""}
            onChange={(e) => handleChange(e)}
            required
            className="form-control"
            label="Hashtag"
            error={!updateStory.hastag && error.hastag}
          />
        </div>
        <div className="form-group mb-2">
          <TextArea
            name="description"
            type="text"
            placeholder="Write here..."
            value={updateStory?.description || ""}
            onChange={(e) => handleChange(e)}
            required
            className="form-control"
            label="Description"
            error={!updateStory.description && error.description}
          />
        </div>

        {/* Post Type Selection */}
        <div className="form-group mb-2">
          <FormControl component="fieldset">
            <FormLabel component="legend">Post Type</FormLabel>
            <RadioGroup
              row
              value={postType}
              onChange={(e) => setPostType(e.target.value)}
            >
              <FormControlLabel value="image" control={<Radio />} label="Image" />
              <FormControlLabel value="audio" control={<Radio />} label="Audio" />
              <FormControlLabel value="video" control={<Radio />} label="Video" />
            </RadioGroup>
          </FormControl>
        </div>

        {/* Scheduling Options */}
        <div className="form-group mb-2">
          <FormControl component="fieldset">
            <FormLabel component="legend">Publishing Options</FormLabel>
            <RadioGroup
              row
              value={isScheduled ? "scheduled" : "immediate"}
              onChange={(e) => setIsScheduled(e.target.value === "scheduled")}
            >
              <FormControlLabel value="immediate" control={<Radio />} label="Publish Now" />
              <FormControlLabel value="scheduled" control={<Radio />} label="Schedule Post" />
            </RadioGroup>
          </FormControl>
        </div>

        {/* Schedule Date/Time Input */}
        {isScheduled && (
          <div className="form-group mb-2">
            <Input
              type="datetime-local"
              name="scheduledAt"
              value={scheduledAt}
              onChange={(e) => setScheduledAt(e.target.value)}
              required
              className="form-control"
              label="Schedule Date & Time"
              error={error.scheduledAt}
            />
          </div>
        )}
        
        {/* Audio Upload - Only for Audio Post Type */}
        {postType === "audio" && (
          <>
            <div className="form-group my-2">
              <Input
                type="file"
                accept="audio/mp3"
                name="musicMedia"
                required
                onChange={(e) => audioHandler(e)}
                placeholder="Upload Music"
                className={`form-control `}
                label={'Upload Music'}
                error={!music && error.music}
              />
            </div>

            {music && (
              <div className="music-player">
                <audio
                  src={music?.name ? URL.createObjectURL(music) : music}
                  controls
                  controlsList="nodownload"
                />
                {musicDuration && (
                  <p style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                    Duration: {musicDuration}
                  </p>
                )}
              </div>
            )}
          </>
        )}

        {/* Video Thumbnail Upload - Only for Video Post Type */}
        {postType === "video" && (
          <div className="form-group my-2">
            <Input
              type="file"
              accept="image/*"
              name="exploreImage"
              onChange={(e) => {
                const file = e.target.files[0];
                if (file) {
                  setExploreImage(file);
                }
              }}
              placeholder="Video Thumbnail Image"
              className={`form-control `}
              label={"Video Thumbnail Image"}
            />
          </div>
        )}
        {/* Media Upload - Conditional based on post type */}
        <div className="form-group my-2">
          <Input
            type="file"
            accept={
              postType === "image" ? "image/*" :
              postType === "audio" ? "image/*" :
              postType === "video" ? "video/*" : "image/*,video/*"
            }
            name="categoryImage"
            multiple={postType === "image" || postType === "audio"}
            required
            onChange={(e) => imageHandler(e)}
            placeholder={
              postType === "image" ? "Upload Images" :
              postType === "audio" ? "Upload Images" :
              postType === "video" ? "Upload Video" : "Upload Media"
            }
            className={`form-control `}
            label={
              postType === "image" ? "Images" :
              postType === "audio" ? "Images" :
              postType === "video" ? "Video" : "Media"
            }
            error={error.image}
          />
        </div>

        <div>
          {images && images.length > 0 && (
            <div style={{ marginTop: '10px' }}>
              <h6 style={{ marginBottom: '10px', color: '#666' }}>
                Selected {postType === "image" ? "Images" : postType === "audio" ? "Images" : "Video"} ({images.length} files)
              </h6>
              {images.map((img, index) => {
                return (
                  <div 
                    key={`${img.name}-${index}`} 
                    style={{ 
                      marginBottom: '15px',
                      position: 'relative',
                      border: '1px solid #ddd',
                      borderRadius: '8px',
                      padding: '10px',
                      backgroundColor: '#f9f9f9'
                    }}
                  >
                    <button
                      onClick={() => removeImage(index)}
                      style={{
                        position: 'absolute',
                        top: '5px',
                        right: '5px',
                        background: '#ff4444',
                        color: 'white',
                        border: 'none',
                        borderRadius: '50%',
                        width: '25px',
                        height: '25px',
                        cursor: 'pointer',
                        fontSize: '12px',
                        zIndex: 10
                      }}
                      title="Remove this file"
                    >
                      ×
                    </button>
                    
                    <div style={{ marginBottom: '5px', fontSize: '12px', color: '#666' }}>
                      {img.name || img.filename || 'Media File'}
                    </div>
                    
                    {(videoFileRegex.test(img?.name) || (img?.url && videoFileRegex.test(img.url))) && postType === "video" ? (
                      <Fragment>
                        <div className="video-container">
                          <VideoPreview videoUrl={img?.name ? URL.createObjectURL(img) : img.url} />
                        </div>

                        {img?.name && (
                          <div className="video-thumbnail">
                            <VideoThumbnail
                              videoUrl={URL.createObjectURL(img)}
                              thumbnailHandler={(thumbnail) =>
                                setVideoThumbnail(thumbnail)
                              }
                              renderThumbnail={false}
                              snapshotAtTime={4}
                              width={1080}
                              height={1920}
                            />
                          </div>
                        )}
                      </Fragment>
                    ) : (
                      <Avatar
                        variant="square"
                        sx={{
                          width: "100%",
                          borderRadius: 1,
                          height: 140,
                          border: "1px solid #FF8989",
                        }}
                        src={img?.name ? URL.createObjectURL(img) : img.url}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </ModalBox>
  );
};

export default EditStories;
